import React from 'react'

interface CardProps {
  children: React.ReactNode
  title?: string
  subtitle?: string
  action?: React.ReactNode
  className?: string
  padding?: 'none' | 'sm' | 'md' | 'lg'
  border?: boolean
}

const paddingStyles = {
  none: '',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
}

export function Card({
  children,
  title,
  subtitle,
  action,
  className = '',
  padding = 'md',
  border = true,
}: CardProps) {
  return (
    <div
      className={`
        bg-white rounded-xl shadow-sm
        ${border ? 'border border-gray-200' : ''}
        ${className}
      `}
    >
      {(title || action) && (
        <div className={`flex items-center justify-between border-b border-gray-200 ${paddingStyles[padding]}`}>
          <div>
            {title && <h3 className="text-lg font-semibold text-gray-800">{title}</h3>}
            {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
          </div>
          {action && <div>{action}</div>}
        </div>
      )}
      <div className={paddingStyles[padding]}>{children}</div>
    </div>
  )
}

interface StatCardProps {
  title: string
  value: string | number
  icon?: string
  trend?: {
    value: number
    label: string
  }
  warning?: boolean
  positive?: boolean
}

export function StatCard({ title, value, icon, trend, warning, positive: _positive }: StatCardProps) {
  return (
    <div
      className={`
        bg-white rounded-xl shadow p-5
        ${warning ? 'border-l-4 border-amber-400' : ''}
      `}
    >
      {icon && <div className="text-2xl mb-1">{icon}</div>}
      <div className="text-3xl font-bold text-gray-800">{value}</div>
      <div className="text-sm text-gray-500 mt-1">{title}</div>
      {trend && (
        <div className={`mt-2 text-sm ${trend.value >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {trend.value >= 0 ? '↑' : '↓'} {Math.abs(trend.value)}% {trend.label}
        </div>
      )}
    </div>
  )
}

interface InfoCardProps {
  title: string
  children: React.ReactNode
  className?: string
}

export function InfoCard({ title, children, className = '' }: InfoCardProps) {
  return (
    <div className={`bg-gray-50 rounded-xl p-4 ${className}`}>
      <h4 className="text-sm font-medium text-gray-700 mb-2">{title}</h4>
      {children}
    </div>
  )
}
